import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { useCompany } from '@/company/context';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import copyCatalog from '@/i18n/ns/connection-access';
import commonCatalog from '@/i18n/ns/connect-access';
import type { ScopedConnectionItem } from '../connection-api';
import type { ConnectionAccessChoice } from '../connection-read-access/api';
import { ConnectionPolicyFields, useConnectionPeople } from './policy-fields';

export function ConnectSheet({
  item,
  companyName,
  canPrivate,
  canCompany,
  onCancel,
  onContinue,
}: {
  item: ScopedConnectionItem;
  companyName: string;
  canPrivate: boolean;
  canCompany: boolean;
  onCancel: () => void;
  onContinue: (target: ScopedConnectionItem, choice: ConnectionAccessChoice) => void;
}) {
  const copy = useNamespaceCopy(copyCatalog);
  const common = useNamespaceCopy(commonCatalog);
  const company = useCompany().data;
  const people = useConnectionPeople();
  const [choice, setChoice] = useState<ConnectionAccessChoice>({
    audience: item.audience,
    people: [],
    changes: 'ask',
  });
  const physicalAudience = choice.audience === 'company' ? 'company' : 'private';
  const available = physicalAudience === 'company' ? canCompany : canPrivate;
  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="pc-portal ws-access-sheet">
        <header>
          <DialogTitle>{formatCopy(copy.sheetTitleConnect, { name: item.name })}</DialogTitle>
          <DialogDescription>{common.connectBody}</DialogDescription>
        </header>
        <div className="ws-access-sheet-body">
          <ConnectionPolicyFields
            value={choice}
            onChange={setChoice}
            companyName={companyName}
            canCompany={canCompany}
            ownerUserId={company?.membership?.userId ?? null}
            people={people}
          />
        </div>
        <footer>
          <Button variant="outline" onClick={onCancel}>
            {common.cancel}
          </Button>
          <Button
            disabled={!available || (choice.audience === 'people' && !choice.people.length)}
            onClick={() => onContinue({ ...item, audience: physicalAudience }, choice)}
          >
            {formatCopy(common.continueTo, { service: item.name })}
          </Button>
        </footer>
      </DialogContent>
    </Dialog>
  );
}

export function ConnectSheetHost({
  connections,
  companyName,
}: {
  connections: {
    sheet: ScopedConnectionItem | null;
    sheetAudiences: { canPrivate: boolean; canCompany: boolean };
    continueConnect: (target: ScopedConnectionItem, choice: ConnectionAccessChoice) => void;
    cancelSheet: () => void;
  };
  companyName: string;
}) {
  const { sheet } = connections;
  return sheet ? (
    <ConnectSheet
      key={`${sheet.audience}:${sheet.id}`}
      item={sheet}
      companyName={companyName}
      {...connections.sheetAudiences}
      onCancel={connections.cancelSheet}
      onContinue={connections.continueConnect}
    />
  ) : null;
}
